import React, { useState, useRef, useEffect } from 'react';
import HeroRotator from './HeroRotator';

const MAIN_APP = process.env.REACT_APP_API_URL || '';
const PUBLIC_SITE = process.env.REACT_APP_PUBLIC_SITE_ORIGIN || 'https://ehandshake.com';

/* ─── Reusable pieces ─── */

function SectionHeading({ eyebrow, title, subtitle }) {
  return (
    <div className="text-center max-w-3xl mx-auto mb-6">
      {eyebrow && (
        <span className="inline-block text-[#00aef7] text-[11px] font-bold uppercase tracking-widest mb-1">
          {eyebrow}
        </span>
      )}
      <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">{title}</h2>
      {subtitle && <p className="mt-2 text-gray-500 text-sm md:text-base leading-relaxed">{subtitle}</p>}
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="w-10 h-10 rounded-lg bg-[#00aef7]/10 flex items-center justify-center text-[#00aef7] mb-3 mx-auto">
        {icon}
      </div>
      <h3 className="text-sm font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
    </div>
  );
}

function StepCard({ number, title, description }) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00aef7] text-white flex items-center justify-center font-bold text-xs">
        {number}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-0.5">{title}</h3>
        <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function ScreenshotPlaceholder({ label }) {
  return (
    <div className="bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 aspect-video flex items-center justify-center">
      <div className="text-center">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-8 h-8 text-gray-300 mx-auto mb-1">
          <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
        </svg>
        <span className="text-[10px] text-gray-400 font-medium">{label}</span>
      </div>
    </div>
  );
}

function PlatformRow({ icon, bgColor, name, desc }) {
  return (
    <div className="flex gap-3 items-start">
      <div className={`w-8 h-8 rounded-lg ${bgColor} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div>
        <h3 className="text-xs font-semibold text-gray-900">{name}</h3>
        <p className="text-[11px] text-gray-500">{desc}</p>
      </div>
    </div>
  );
}

function ReachCard({ platform, accent, seen, caption }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-3 flex flex-col">
      <div className="flex items-center gap-1.5 mb-2">
        <div className="w-2 h-2 rounded-full" style={{ background: accent }} />
        <h3 className="text-xs font-bold text-gray-900 tracking-wide">{platform}</h3>
      </div>

      <div className="mb-2">
        <div className="leading-none">
          <span className="text-2xl font-bold" style={{ color: accent }}>{seen}</span>
          <span className="text-xl font-bold text-gray-300"> / 100</span>
        </div>
        <p className="text-[10px] text-gray-500 mt-1">followers reached, on average</p>
      </div>

      <div className="grid grid-cols-10 gap-[2px] mb-2" aria-hidden="true">
        {Array.from({ length: 100 }).map((_, i) => (
          <div
            key={i}
            className="aspect-square rounded-full"
            style={{ background: i < seen ? accent : '#e5e7eb' }}
          />
        ))}
      </div>

      <p className="text-[10px] text-gray-500 leading-relaxed mt-auto">
        {caption}
      </p>
    </div>
  );
}

/* ─── Profession icons (Heroicons-style outline, 24x24 viewBox) ─── */
const ICON_PROPS = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  viewBox: '0 0 24 24',
  className: 'w-full h-full',
  'aria-hidden': true,
};
const PlumberIcon = () => (
  <svg viewBox="0 -960 960 960" fill="currentColor" className="w-full h-full" aria-hidden="true">
    <path d="M771-593 630-734l-85 84-85-84 113-114q12-12 27-17.5t30-5.5q16 0 30.5 5.5T686-848l85 85q18 17 26.5 39.5T806-678q0 23-8.5 45T771-593ZM220-409q-18-18-18-42.5t18-42.5l98-99 85 85-99 99q-17 18-41.5 18T220-409Zm-43 297q-11-12-17-26.5t-6-30.5q0-16 5.5-30.5T177-226l283-282-127-128q-18-17-18-41.5t18-42.5q17-18 42-18t43 18l127 127 57-57 112 114q12 12 12 28t-12 28q-12 12-28 12t-28-12L290-112q-12 12-26.5 17.5T234-89q-15 0-30-6t-27-17Z" />
  </svg>
);
const RealtorIcon = () => (
  <svg viewBox="0 -960 960 960" fill="currentColor" className="w-full h-full" aria-hidden="true">
    <path d="M760-400v-260L560-800 360-660v60h-80v-100l280-200 280 200v300h-80ZM560-800Zm20 160h40v-40h-40v40Zm-80 0h40v-40h-40v40Zm80 80h40v-40h-40v40Zm-80 0h40v-40h-40v40ZM280-220l278 76 238-74q-5-9-14.5-15.5T760-240H558q-27 0-43-2t-33-8l-93-31 22-78 81 27q17 5 40 8t68 4q0-11-6.5-21T578-354l-234-86h-64v220ZM40-80v-440h304q7 0 14 1.5t13 3.5l235 87q33 12 53.5 42t20.5 66h80q50 0 85 33t35 87v40L560-60l-280-78v58H40Zm80-80h80v-280h-80v280Z" />
  </svg>
);
const ElectricianIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M13 2 4 14h6l-1 8 9-12h-6z" />
  </svg>
);
const RooferIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="m2 11 10-7 10 7" />
    <path d="M5 11v9h14v-9" />
    <path d="M5 14h14M5 17h14" />
  </svg>
);
const HvacIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M12 2v20M2 12h20M5 5l14 14M5 19 19 5" />
  </svg>
);
const MortgageIcon = () => (
  <svg viewBox="0 -960 960 960" fill="currentColor" className="w-full h-full" aria-hidden="true">
    <path d="M200-280v-280h80v280h-80Zm240 0v-280h80v280h-80ZM80-120v-80h800v80H80Zm600-160v-280h80v280h-80ZM80-640v-80l400-200 400 200v80H80Zm178-80h444-444Zm0 0h444L480-830 258-720Z" />
  </svg>
);
const InsuranceIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);
const TaxIcon = () => (
  <svg {...ICON_PROPS}>
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <rect x="6" y="4" width="12" height="4" rx="1" />
    <circle cx="8" cy="12" r="0.5" fill="currentColor" />
    <circle cx="12" cy="12" r="0.5" fill="currentColor" />
    <circle cx="16" cy="12" r="0.5" fill="currentColor" />
    <circle cx="8" cy="16" r="0.5" fill="currentColor" />
    <circle cx="12" cy="16" r="0.5" fill="currentColor" />
    <circle cx="16" cy="16" r="0.5" fill="currentColor" />
  </svg>
);
const LandscaperIcon = () => (
  <svg viewBox="0 -960 960 960" fill="currentColor" className="w-full h-full" aria-hidden="true">
    <path d="m46-160 138-276q10-20 28.5-32t41.5-12q24 0 44 12.5t29 35.5l27 66q2 6 9 5.5t9-6.5l86-287q14-48 53.5-77t89.5-29q49 0 87.5 28.5T742-657l173 497h-85L666-632q-8-23-25-35.5T601-680q-23 0-40.5 13T535-631l-86 287q-9 28-32.5 46T363-280q-27 0-50-14.5T280-335l-27-66-118 241H46Zm194-400q-50 0-85-35.5T120-680q0-50 35-85t85-35q50 0 85 35t35 85q0 49-35 84.5T240-560Zm0-80q17 0 28.5-11.5T280-680q0-17-11.5-28.5T240-720q-17 0-28.5 11.5T200-680q0 17 11.5 28.5T240-640Zm123 360ZM240-680Z" />
  </svg>
);
const MechanicIcon = () => (
  <svg viewBox="0 -960 960 960" fill="currentColor" className="w-full h-full" aria-hidden="true">
    <path d="M440-80v-120H160v-80h640v80H520v120h-80Zm-51.5-431.5Q400-523 400-540t-11.5-28.5Q377-580 360-580t-28.5 11.5Q320-557 320-540t11.5 28.5Q343-500 360-500t28.5-11.5Zm240 0Q640-523 640-540t-11.5-28.5Q617-580 600-580t-28.5 11.5Q560-557 560-540t11.5 28.5Q583-500 600-500t28.5-11.5ZM200-616l66-192q5-14 16.5-23t25.5-9h344q14 0 25.5 9t16.5 23l66 192v264q0 14-9 23t-23 9h-16q-14 0-23-9t-9-23v-48H280v48q0 14-9 23t-23 9h-16q-14 0-23-9t-9-23v-264Zm106-64h348l-28-80H334l-28 80Zm-26 80v120-120Zm0 120h400v-120H280v120Z" />
  </svg>
);

const PROFESSIONS = [
  { name: 'Plumber',         icon: <PlumberIcon /> },
  { name: 'Realtor',         icon: <RealtorIcon /> },
  { name: 'Electrician',     icon: <ElectricianIcon /> },
  { name: 'Roofer',          icon: <RooferIcon /> },
  { name: 'HVAC Tech',       icon: <HvacIcon /> },
  { name: 'Mortgage Broker', icon: <MortgageIcon /> },
  { name: 'Insurance Agent', icon: <InsuranceIcon /> },
  { name: 'Tax Preparer',    icon: <TaxIcon /> },
  { name: 'Landscaper',      icon: <LandscaperIcon /> },
  { name: 'Auto Mechanic',   icon: <MechanicIcon /> },
];

/* ─── 100-dot grid (10x10) — first N highlighted in brand cyan ─── */
function DotGrid100({ highlighted = 5, mini = false }) {
  const dot = mini ? 'w-1 h-1' : 'w-2 h-2';
  const gap = mini ? 'gap-px' : 'gap-[2px]';
  return (
    <div className={`grid grid-cols-10 ${gap} w-fit`}>
      {Array.from({ length: 100 }).map((_, i) => (
        <div
          key={i}
          className={`${dot} rounded-full`}
          style={{ background: i < highlighted ? '#00aef7' : '#e5e7eb' }}
        />
      ))}
    </div>
  );
}

/* ─── ProfessionCard — used in Drip Networking math grid ─── */
function ProfessionCard({ name, icon }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-2.5 flex flex-col items-center text-center">
      <div className="w-7 h-7 text-[#00aef7] mb-1.5">{icon}</div>
      <p className="text-[10px] font-semibold text-gray-700 leading-tight mb-2 min-h-[24px] flex items-center justify-center">
        {name}
      </p>
      <DotGrid100 highlighted={5} mini />
      <p className="text-[10px] font-bold text-gray-900 mt-1.5">
        <span className="text-[#00aef7]">5</span> / 100
      </p>
    </div>
  );
}

/* ─── BillboardScene — full-size single-billboard scene for Drip Marketing ─── */
function BillboardScene() {
  return (
    <svg viewBox="0 0 280 220" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {/* Sky */}
      <rect x="0" y="0" width="280" height="155" fill="#f0f9ff" />
      {/* Sun */}
      <circle cx="240" cy="32" r="14" fill="#fbbf24" />
      <g stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" opacity="0.55">
        <line x1="240" y1="10" x2="240" y2="6" />
        <line x1="223" y1="15" x2="220" y2="11" />
        <line x1="257" y1="15" x2="260" y2="11" />
        <line x1="218" y1="32" x2="214" y2="32" />
        <line x1="262" y1="32" x2="266" y2="32" />
      </g>
      {/* Distant buildings — varied colors */}
      <rect x="10" y="120" width="30" height="35" fill="#e2e8f0" />
      <rect x="42" y="98" width="24" height="57" fill="#fde68a" />
      <rect x="68" y="125" width="22" height="30" fill="#cbd5e1" />
      <rect x="195" y="115" width="28" height="40" fill="#bfdbfe" />
      <rect x="225" y="98" width="22" height="57" fill="#cbd5e1" />
      <rect x="248" y="125" width="22" height="30" fill="#fde68a" />
      {/* Grass strip */}
      <rect x="0" y="155" width="280" height="6" fill="#86efac" />
      {/* Road */}
      <rect x="0" y="161" width="280" height="59" fill="#475569" />
      <line x1="0" y1="190" x2="280" y2="190" stroke="white" strokeWidth="2" strokeDasharray="14,12" opacity="0.7" />
      {/* Billboard pole */}
      <rect x="135" y="100" width="6" height="61" fill="#334155" />
      <rect x="138" y="100" width="6" height="61" fill="#1e293b" />
      {/* Billboard frame */}
      <rect x="80" y="20" width="120" height="85" fill="white" stroke="#334155" strokeWidth="2" rx="3" />
      {/* Mock post: avatar + name + body + image + reactions */}
      <circle cx="92" cy="35" r="5" fill="#cbd5e1" />
      <rect x="100" y="32" width="40" height="3" fill="#94a3b8" rx="1.5" />
      <rect x="100" y="38" width="25" height="2" fill="#cbd5e1" rx="1" />
      <rect x="86" y="48" width="108" height="2.5" fill="#94a3b8" rx="1.25" />
      <rect x="86" y="54" width="90" height="2.5" fill="#94a3b8" rx="1.25" />
      <rect x="86" y="60" width="108" height="28" fill="#e2e8f0" rx="2" />
      <circle cx="92" cy="98" r="2" fill="#00aef7" />
      <circle cx="100" cy="98" r="2" fill="#cbd5e1" />
      <circle cx="108" cy="98" r="2" fill="#cbd5e1" />
      {/* Vehicles — 3 in lower lane (close, larger) + 2 in upper lane (far, smaller, going opposite direction) */}
      {/* === LOWER LANE (3 vehicles, larger) === */}
      {/* L1: Red sedan */}
      <g>
        <rect x="15" y="204" width="30" height="8" fill="#dc2626" rx="2" />
        <path d="M19 204 L22 196 L38 196 L42 204 Z" fill="#b91c1c" />
        <rect x="23" y="198" width="6" height="5" fill="#cbd5e1" rx="0.5" />
        <rect x="31" y="198" width="6" height="5" fill="#cbd5e1" rx="0.5" />
        <circle cx="22" cy="214" r="3" fill="#0f172a" />
        <circle cx="40" cy="214" r="3" fill="#0f172a" />
      </g>
      {/* L2: White pickup truck */}
      <g>
        <rect x="131" y="201" width="22" height="11" fill="#f1f5f9" stroke="#94a3b8" strokeWidth="0.5" rx="1" />
        <rect x="120" y="197" width="13" height="15" fill="#f1f5f9" stroke="#94a3b8" strokeWidth="0.5" rx="1.5" />
        <rect x="122" y="199" width="9" height="5" fill="#cbd5e1" rx="0.5" />
        <circle cx="125" cy="214" r="3" fill="#0f172a" />
        <circle cx="150" cy="214" r="3" fill="#0f172a" />
      </g>
      {/* L3: Green semi truck */}
      <g>
        <rect x="200" y="194" width="55" height="18" fill="#16a34a" rx="0.5" />
        <rect x="185" y="200" width="16" height="12" fill="#15803d" rx="1" />
        <rect x="187" y="202" width="11" height="4" fill="#cbd5e1" rx="0.5" />
        <circle cx="190" cy="214" r="2.5" fill="#0f172a" />
        <circle cx="210" cy="214" r="2.5" fill="#0f172a" />
        <circle cx="245" cy="214" r="2.5" fill="#0f172a" />
      </g>
      {/* === UPPER LANE (2 vehicles, smaller — going opposite direction).
           4px higher than initial draft so they sit centered in their lane. === */}
      {/* U1: Blue compact */}
      <g>
        <rect x="60" y="176" width="22" height="6" fill="#2563eb" rx="1.5" />
        <path d="M63 176 L65 171 L78 171 L80 176 Z" fill="#1d4ed8" />
        <rect x="67" y="172.5" width="4" height="3" fill="#cbd5e1" rx="0.3" />
        <rect x="73" y="172.5" width="4" height="3" fill="#cbd5e1" rx="0.3" />
        <circle cx="65" cy="183" r="2" fill="#0f172a" />
        <circle cx="79" cy="183" r="2" fill="#0f172a" />
      </g>
      {/* U2: Black SUV */}
      <g>
        <rect x="170" y="174" width="28" height="9" fill="#1e293b" rx="1.5" />
        <rect x="173" y="176" width="10" height="4" fill="#94a3b8" rx="0.3" />
        <rect x="184" y="176" width="10" height="4" fill="#94a3b8" rx="0.3" />
        <circle cx="176" cy="183.5" r="2" fill="#0f172a" />
        <circle cx="192" cy="183.5" r="2" fill="#0f172a" />
      </g>
    </svg>
  );
}

/* ─── BillboardSceneMini — compact version for the 4×1 row in Drip Networking.
       sunX/sunY override sun position so each of the 4 minis can show a
       different time of day; buildingPalette varies the silhouette colors so
       each scene reads as a different "neighborhood." ─── */
function BillboardSceneMini({
  sunX = 175,
  sunY = 25,
  buildingPalette = ['#e2e8f0', '#fde68a', '#cbd5e1', '#bfdbfe'],
  vehicleColors = ['#dc2626', '#1e293b', '#f1f5f9', '#2563eb', '#eab308'],
  vehicleX = [8, 68, 120, 40, 130],
}) {
  const [b1, b2, b3, b4] = buildingPalette;
  const [v1, v2, v3, v4, v5] = vehicleColors;
  const [x1, x2, x3, x4, x5] = vehicleX;
  return (
    <svg viewBox="0 0 200 180" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {/* Sky */}
      <rect x="0" y="0" width="200" height="125" fill="#f0f9ff" />
      {/* Sun */}
      <circle cx={sunX} cy={sunY} r="9" fill="#fbbf24" />
      {/* Distant buildings — varied colors per scene */}
      <rect x="10" y="100" width="22" height="25" fill={b1} />
      <rect x="34" y="88" width="20" height="37" fill={b2} />
      <rect x="155" y="95" width="22" height="30" fill={b3} />
      <rect x="178" y="86" width="20" height="39" fill={b4} />
      {/* Grass strip */}
      <rect x="0" y="125" width="200" height="5" fill="#86efac" />
      {/* Road */}
      <rect x="0" y="130" width="200" height="50" fill="#475569" />
      <line x1="0" y1="155" x2="200" y2="155" stroke="white" strokeWidth="1.5" strokeDasharray="10,8" opacity="0.7" />
      {/* Pole */}
      <rect x="97" y="80" width="4" height="50" fill="#334155" />
      <rect x="99" y="80" width="4" height="50" fill="#1e293b" />
      {/* Billboard frame */}
      <rect x="55" y="15" width="90" height="68" fill="white" stroke="#334155" strokeWidth="1.5" rx="2" />
      {/* Mock post (compressed) */}
      <circle cx="63" cy="26" r="3" fill="#cbd5e1" />
      <rect x="69" y="24" width="28" height="2" fill="#94a3b8" rx="1" />
      <rect x="69" y="29" width="18" height="1.5" fill="#cbd5e1" rx="0.75" />
      <rect x="60" y="36" width="80" height="2" fill="#94a3b8" rx="1" />
      <rect x="60" y="40" width="60" height="2" fill="#94a3b8" rx="1" />
      <rect x="60" y="46" width="80" height="22" fill="#e2e8f0" rx="1.5" />
      <circle cx="65" cy="74" r="1.5" fill="#00aef7" />
      <circle cx="71" cy="74" r="1.5" fill="#cbd5e1" />
      <circle cx="77" cy="74" r="1.5" fill="#cbd5e1" />
      {/* Vehicles — 3 lower (close, larger) + 2 upper (far, smaller, going opposite direction).
           Each vehicle's <g> uses translate so the parent can vary x positions per scene
           without rewriting inner coords. Upper-lane vehicles sit centered in their lane
           (4px higher than lower-lane row baseline). */}
      {/* === LOWER LANE === */}
      {/* L1: sedan */}
      <g transform={`translate(${x1}, 0)`}>
        <rect x="0" y="164" width="20" height="6" fill={v1} rx="1.5" />
        <path d="M3 164 L5 159 L16 159 L18 164 Z" fill={v1} />
        <circle cx="5" cy="172" r="2" fill="#0f172a" />
        <circle cx="16" cy="172" r="2" fill="#0f172a" />
      </g>
      {/* L2: SUV */}
      <g transform={`translate(${x2}, 0)`}>
        <rect x="0" y="161" width="22" height="9" fill={v2} rx="1.5" />
        <rect x="3" y="163" width="6" height="3" fill="#cbd5e1" rx="0.3" />
        <rect x="11" y="163" width="6" height="3" fill="#cbd5e1" rx="0.3" />
        <circle cx="5" cy="172" r="2" fill="#0f172a" />
        <circle cx="18" cy="172" r="2" fill="#0f172a" />
      </g>
      {/* L3: pickup truck */}
      <g transform={`translate(${x3}, 0)`}>
        <rect x="8" y="163" width="20" height="7" fill={v3} stroke="#94a3b8" strokeWidth="0.3" rx="0.5" />
        <rect x="0" y="160" width="10" height="10" fill={v3} stroke="#94a3b8" strokeWidth="0.3" rx="0.5" />
        <rect x="2" y="161.5" width="6" height="3" fill="#cbd5e1" rx="0.3" />
        <circle cx="3" cy="172" r="2" fill="#0f172a" />
        <circle cx="23" cy="172" r="2" fill="#0f172a" />
      </g>
      {/* === UPPER LANE (4px higher than before so vehicles sit centered in the lane) === */}
      {/* U1: small car */}
      <g transform={`translate(${x4}, 0)`}>
        <rect x="0" y="142" width="16" height="4.5" fill={v4} rx="1" />
        <path d="M2 142 L4 138 L12 138 L14 142 Z" fill={v4} />
        <circle cx="4" cy="147.5" r="1.5" fill="#0f172a" />
        <circle cx="13" cy="147.5" r="1.5" fill="#0f172a" />
      </g>
      {/* U2: small car */}
      <g transform={`translate(${x5}, 0)`}>
        <rect x="0" y="142" width="18" height="4.5" fill={v5} rx="1" />
        <path d="M2 142 L4 138 L14 138 L16 142 Z" fill={v5} />
        <circle cx="4" cy="147.5" r="1.5" fill="#0f172a" />
        <circle cx="14" cy="147.5" r="1.5" fill="#0f172a" />
      </g>
    </svg>
  );
}

/* ─── Design Center showcase videos served from CloudFront
 * (behavior /videos/* → S3 bucket ehandshake-public-assets/videos/designcenter/).
 * Override via REACT_APP_DESIGN_VIDEO_BASE_URL if the CDN ever moves. ── */
const DESIGN_VIDEO_BASE_URL =
  process.env.REACT_APP_DESIGN_VIDEO_BASE_URL ||
  'https://ehandshake.com/videos/designcenter';

/* ─── Design Center showcase steps (4 chapters: design → preview → landing → publish)
 * A 5th "sharing center" segment plays at the tail of the video but isn't a
 * card here — it acts as the visual handoff to the next homepage section.
 * Each step has a `videoFile` once its clip is cut + uploaded to S3; steps
 * without one fall back to the play-button placeholder. ── */
const DESIGN_CENTER_STEPS = [
  {
    key: 'design',
    label: 'AI-powered Design Center',
    title: 'Design your creation',
    description: 'AI suggests headlines, captions, and visuals tuned to your profession.',
    videoFile: 'DesignCenter1.mp4',
    benefits: [
      '8 idea categories to get your creative juices flowing.',
      'Design once — we format it natively for Facebook, Instagram, X, YouTube, LinkedIn, and TikTok.',
      'Fully describe your idea and let the eHandshake AI design it for you.',
      'Or tap "Inspirations" for fresh AI-generated ideas to post.',
      'Answer a few quick questions — your headline, per-platform captions, hashtags, and emoji are generated automatically.',
      '3.2 million royalty-free stock images and videos at your fingertips.',
      'Visuals tuned to your profession are surfaced first.',
      'Or upload your own photo or video.',
    ],
  },
  {
    key: 'preview',
    label: 'Preview Social Posts',
    title: 'Preview every platform',
    description: 'See how it lands on Facebook, Instagram, X, LinkedIn, TikTok, and YouTube — native format each.',
    videoFile: 'DesignCenter2.mp4',
    benefits: [
      'Fine-tune headline position on each platform for the best fit.',
      'Customize captions, hashtags, and emoji per platform so each post feels native.',
      'Turn static images into short videos for YouTube and TikTok posts.',
      'When every platform looks right, continue on to build the landing page your social links click through to.',
    ],
  },
  {
    key: 'landing',
    label: 'Create Landing Page',
    title: 'Create a landing page',
    description: 'A full mini-site for the creation — AI writes the copy, you approve.',
    videoFile: 'DesignCenter3.mp4',
    benefits: [
      'Link your own landing page, or let our AI craft a fully customizable one for you.',
      'Link out to any public page — YouTube, Facebook, Pinterest, your online store, or a blog on your own website.',
      'Drop in more stock images and videos, or upload your own.',
      'Choose from 4 pre-designed templates: Bold Hero, Grid Gallery, Feature Strip, or Compact Editorial.',
      'Match the accent color to your brand.',
      'Edit any AI-generated text, add as many sections as you want, and wire up call-to-action links.',
    ],
  },
  {
    key: 'publish',
    label: 'Publish Your Creation',
    title: 'Publish to the sharing center',
    description: 'Native-format posts go live on every platform you\'ve connected.',
    videoFile: 'SharingCenter.mp4',
    benefits: [
      'Publish one creation to every platform bucket — already formatted for each.',
      'See the social post and its landing page side-by-side, per platform.',
      'Browse other users\' posts per platform — one-click copy-link or jump straight to the live post.',
      'Publish to Facebook, Instagram, X, YouTube, LinkedIn, TikTok, and email — all from one place.',
      'Track likes, comments, shares, and views per platform.',
      'Recommend and share a teammate\'s profile, company profile, or any team profile.',
    ],
  },
];

const videoUrlForStep = (step) =>
  step?.videoFile ? `${DESIGN_VIDEO_BASE_URL}/${step.videoFile}` : null;

/* ─── Design Center Showcase ───────────────────────────────────────────────
 * Apple-style layout: info text (left col) + cards row above video (right col).
 * Behavior:
 *   - activeIdx  = last-clicked card ("pinned"). Starts at 0.
 *   - hoverIdx   = currently hovered card. null when not hovering.
 *   - displayIdx = hoverIdx ?? activeIdx. Drives left-column text AND the
 *                  video-area thumbnail preview (before first play).
 *   - hasStarted = false on first load. Play button overlay shown until the
 *                  user clicks play OR any card.
 *   - After hasStarted=true, card clicks immediately swap to that segment.
 *     Hover while playing updates ONLY the left-column text; the video keeps
 *     playing its current segment.
 * ────────────────────────────────────────────────────────────────────────── */
function DesignCenterShowcase() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [hoverIdx,  setHoverIdx]  = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [isEnded,   setIsEnded]   = useState(false);
  const videoRef = useRef(null);

  // Left column follows the hover if present, else the pinned (active) step.
  const displayIdx = hoverIdx !== null ? hoverIdx : activeIdx;

  // Thumbnail shown in the video area when nothing is playing.
  // Once playback has started, the stage is dominated by the active segment
  // regardless of what's hovered (hover only affects the left-column text).
  const thumbnailIdx = hasStarted ? activeIdx : displayIdx;

  const handleCardClick = (i) => {
    setActiveIdx(i);
    setHasStarted(true);
    setIsEnded(false);
  };

  const handleReplay = () => {
    const el = videoRef.current;
    if (!el) return;
    setIsEnded(false);
    el.currentTime = 0;
    el.play().catch(() => {});
  };

  const displayStep   = DESIGN_CENTER_STEPS[displayIdx];
  const thumbnailStep = DESIGN_CENTER_STEPS[thumbnailIdx];
  const videoUrl      = videoUrlForStep(thumbnailStep);

  // Sync the <video> element to the current segment. Before hasStarted, setting
  // src shows the first-frame thumbnail (preload="metadata"). Once hasStarted
  // is true or activeIdx changes, play from the start of the new segment.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !videoUrl) return;
    // Avoid clobbering the stream if the src is already correct.
    const current = el.src.split('?')[0];
    if (current !== videoUrl) {
      el.src = videoUrl;
      el.load();
    }
    if (hasStarted) {
      el.currentTime = 0;
      setIsEnded(false);
      el.play().catch(() => {});
    }
  }, [videoUrl, hasStarted]);

  return (
    <section id="section-2" className="bg-gray-50 border-y border-gray-100">
      <div className="mx-auto w-[90%] max-w-6xl py-10 md:py-14">

        {/* Section header — brand-blue hero title above the 2×2 grid. */}
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-[#00aef7] tracking-tight text-center mb-10 md:mb-14">
          AI-powered Design & Sharing Center
        </h2>

        {/* 2×2 grid. Row 1: left heading + arrow / right cards.
            Row 2: left step info (hover-driven) / right video.
            Columns line up so the heading sits beside the cards and the
            hover-driven info sits beside the video. Stacks on mobile. */}
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_820px] md:grid-rows-[auto_auto] gap-x-8 md:gap-x-10 gap-y-3 md:gap-y-4">

          {/* ─── ROW 1 / LEFT: "Easy 4 step process" heading + arrow ─── */}
          <div className="flex flex-col justify-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#00aef7] leading-[1.1] tracking-tight flex items-center gap-2 md:gap-3">
              <span className="whitespace-nowrap">Easy 4 step process</span>
              {/* Horizontal arrow pointing at the cards on the right. Hidden on
                  mobile since the cards stack below, not beside. */}
              <svg
                className="hidden md:block w-10 h-4 flex-shrink-0"
                viewBox="0 0 100 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M2 10 h 86 M80 2 l 10 8 l -10 8"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </h2>
            <p className="mt-2 text-gray-700 text-sm md:text-base leading-relaxed max-w-md">
              Design a creation and landing page in under 5 minutes with the help of our AI assistant.
            </p>
          </div>

          {/* ─── ROW 1 / RIGHT: 4 minimal step cards ─── */}
          <div className="grid grid-cols-4 gap-2">
            {DESIGN_CENTER_STEPS.map((step, i) => {
              const isActive = i === activeIdx;
              const isHover  = i === hoverIdx;
              return (
                <button
                  key={step.key}
                  type="button"
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx(null)}
                  onClick={() => handleCardClick(i)}
                  className={`px-1 py-2 rounded-lg border-2 bg-white transition-all flex flex-col items-center justify-center gap-1 ${
                    isActive
                      ? 'border-[#00aef7] shadow-md'
                      : isHover
                        ? 'border-gray-400 shadow-sm'
                        : 'border-gray-200'
                  }`}
                >
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                      isActive ? 'bg-[#00aef7] text-white' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {i + 1}
                  </div>
                  <div
                    className={`text-[10px] uppercase tracking-wider font-bold text-center leading-tight ${
                      isActive ? 'text-[#00aef7]' : 'text-gray-500'
                    }`}
                  >
                    {step.label}
                  </div>
                </button>
              );
            })}
          </div>

          {/* ─── ROW 2 / LEFT: step-dependent content.
              If the current step has a `benefits` array, show brand-blue-check
              bullets. Otherwise fall back to the original hover-driven title +
              description block. ─── */}
          <div className="flex items-start min-h-[160px] md:min-h-[360px]">
            {displayStep.benefits ? (
              <ul className="space-y-2.5 md:space-y-3 w-full max-w-md">
                {displayStep.benefits.map((text, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-[#00aef7] text-white flex items-center justify-center shadow-sm">
                      <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.997 8a1 1 0 01-1.414 0l-3.997-4a1 1 0 011.414-1.42L8 12.58l7.29-7.29a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span className="text-gray-700 text-xs md:text-sm leading-snug">
                      {text}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center max-w-sm px-2 mx-auto">
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#00aef7] mb-2">
                  Step {displayIdx + 1}
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-3">
                  {displayStep.title}
                </h3>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  {displayStep.description}
                </p>
              </div>
            )}
          </div>

          {/* ─── ROW 2 / RIGHT: video stage ─── */}
          <div>
            <div
              className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/10 bg-gradient-to-br from-gray-900 via-gray-800 to-black"
              style={{ aspectRatio: '1280 / 608' }}
            >
              {/* Real <video> when this segment has a file uploaded. Before
                  hasStarted, preload="metadata" shows the first frame as
                  thumbnail. Rollovers change thumbnailIdx → useEffect swaps
                  src. Clicking a card or the play button sets hasStarted and
                  the effect calls play(). */}
              {videoUrl && (
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  preload="metadata"
                  controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                  disablePictureInPicture
                  disableRemotePlayback
                  onContextMenu={(e) => e.preventDefault()}
                  onEnded={() => setIsEnded(true)}
                  onClick={() => {
                    // Click-to-pause / click-to-resume on the playing video,
                    // so a user can freeze a frame to read it. No visible
                    // control — the lack of motion is the indicator.
                    const el = videoRef.current;
                    if (!el || !hasStarted) return;
                    if (el.paused) {
                      el.play().catch(() => {});
                    } else {
                      el.pause();
                    }
                  }}
                  className="absolute inset-0 w-full h-full object-cover cursor-pointer"
                />
              )}

              {/* Replay overlay — visible when the current segment has ended.
                  Clear signal that playback is done + one-tap to watch again,
                  since there's no progress bar and autoplay doesn't loop. */}
              {hasStarted && videoUrl && isEnded && (
                <div className="absolute inset-0 flex items-center justify-center transition-opacity">
                  <div className="text-center px-6">
                    <button
                      type="button"
                      onClick={handleReplay}
                      className="group w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#00aef7] hover:bg-[#0098d8] shadow-xl flex items-center justify-center mx-auto mb-3 transition-all hover:scale-105"
                      aria-label="Replay this segment"
                      title="Replay"
                    >
                      <svg className="w-8 h-8 md:w-10 md:h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 12a9 9 0 1 0 3-6.7" />
                        <polyline points="3 4 3 10 9 10" />
                      </svg>
                    </button>
                    {(() => {
                      const completed = DESIGN_CENTER_STEPS[activeIdx];
                      const next = DESIGN_CENTER_STEPS[activeIdx + 1];
                      return (
                        <div className="inline-block bg-white/90 rounded-lg px-4 py-2 shadow-md text-gray-900 text-sm md:text-base font-medium mt-3">
                          <span className="font-semibold">{completed.label}</span>{' '}
                          is complete
                          {next ? (
                            <>
                              {' '}&mdash;{' '}
                              <button
                                type="button"
                                onClick={() => handleCardClick(activeIdx + 1)}
                                className="text-[#00aef7] hover:text-[#0098d8] underline underline-offset-2 font-semibold"
                              >
                                Ready for {next.label}?
                              </button>
                            </>
                          ) : (
                            <> &mdash; click replay to watch again.</>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Play button + label overlay. Shown before hasStarted, also
                  shown for segments with no video uploaded yet. Hidden only
                  when the video is actually playing a real file. No scrim
                  behind it — full first frame is visible. Text gets a
                  drop-shadow so it stays readable over bright frames. */}
              <div
                className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                  hasStarted && videoUrl ? 'opacity-0 pointer-events-none' : 'opacity-100'
                }`}
              >
                <div className="text-center">
                  {!hasStarted && (
                    <button
                      type="button"
                      onClick={() => handleCardClick(thumbnailIdx)}
                      className="group w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#00aef7] hover:bg-[#0098d8] shadow-xl flex items-center justify-center mx-auto mb-3 transition-all hover:scale-105"
                      aria-label="Play walkthrough"
                      disabled={!videoUrl}
                    >
                      <svg className="w-7 h-7 md:w-9 md:h-9 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </button>
                  )}
                  <div className="text-white text-xs md:text-sm font-medium mt-1 [text-shadow:0_1px_3px_rgba(0,0,0,0.7)]">
                    {thumbnailStep.label}
                  </div>
                  {!videoUrl && (
                    <div className="text-white/80 text-[10px] mt-1 [text-shadow:0_1px_2px_rgba(0,0,0,0.7)]">
                      Video coming soon
                    </div>
                  )}
                </div>
              </div>

              {/* Fullscreen toggle — visible once the video is playing. Uses the
                  browser's native requestFullscreen so the user can expand the
                  walkthrough to the full display when they want a better look. */}
              {hasStarted && videoUrl && (
                <button
                  type="button"
                  onClick={() => {
                    const el = videoRef.current;
                    if (!el) return;
                    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
                    if (req) req.call(el).catch(() => {});
                  }}
                  className="absolute bottom-3 right-3 w-9 h-9 rounded-lg bg-black/50 hover:bg-black/75 backdrop-blur-sm flex items-center justify-center text-white transition opacity-80 hover:opacity-100"
                  aria-label="Expand to fullscreen"
                  title="Fullscreen"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />
                  </svg>
                </button>
              )}

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Page ─── */

const Home = () => {
  // Floating "scroll to top" button visibility — only after the user has
  // scrolled past the hero-ish distance so it doesn't crowd the landing view.
  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const handler = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handler, { passive: true });
    handler();
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <main className="flex-grow bg-white">

      {/* ══════════ HERO ══════════ */}
      <HeroRotator />


      {/* ══════════ EMPATHY STORY — 9 PM vignette ══════════ */}
      <section id="section-1" className="bg-[#faf7f2] border-y border-stone-200">
        <div className="mx-auto w-[90%] max-w-3xl py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#00aef7] leading-tight mb-3">
              This is a true story
            </h2>
            <span className="inline-block text-[#00aef7] text-[11px] font-bold uppercase tracking-widest mb-3">
              The Problem Every Small Business Owner Knows
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              It&rsquo;s 9 PM.
            </h2>
          </div>

          <div className="font-serif text-gray-700 text-base md:text-lg leading-relaxed space-y-5">
            <p>
              The lights are off in the shop. You sit down with a cup of coffee
              and decide: tonight, I&rsquo;m going to finally post something about the business on
              social media. Find some new customers...
            </p>
            <p>
              You open Instagram. <em>What do I say?</em> You open Facebook.{' '}
              <em>What photo do I use?</em> You try Canva. You get lost in the options. You google{' '}
              &ldquo;how to post as a plumber&rdquo;. The results are overwhelming. 2
              hours later, you close the laptop and go to bed.
            </p>
            <p>
              Tomorrow you&rsquo;ll be on a Up and at 'em at 7 AM. Marketing will have to wait until the next night, and this viscious cycle will repeat itself endlessly.
            </p>
        
          </div>

          {/* Photo storyboard — 9 PM → 10 PM → 11 PM → 7 AM */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {[
              { time: '9 PM',  label: 'Sit down with good intentions', src: '/images/empathy/empathy-9pm-coffee.jpg',   alt: 'Coffee mug on a home kitchen table in the evening, laptop nearby' },
              { time: '10 PM', label: 'The idea repository is empty',     src: '/images/empathy/empathy-10pm-stuck.jpg',    alt: 'Face lit by a laptop screen, thinking, frustrated' },
              { time: '11 PM', label: 'Close the laptop. It can wait.',    src: '/images/empathy/empathy-11pm-closing.jpg',  alt: 'Laptop being closed in a dimly lit room late at night' },
              { time: '7 AM',  label: 'Back on the job. Again.',       src: '/images/empathy/empathy-7am-jobsite.jpg',   alt: 'Tradesman starting work at a job site in the early morning' },
            ].map(({ time, label, src, alt }) => (
              <figure key={time} className="text-center">
                <div className="overflow-hidden rounded-lg shadow-md ring-1 ring-stone-200/70">
                  <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    className="w-full aspect-[3/2] object-cover"
                  />
                </div>
                <figcaption className="mt-3">
                  <div className="text-sm font-semibold text-gray-800 tracking-wide">{time}</div>
                  <div className="text-[11px] md:text-xs text-gray-500 mt-0.5 leading-snug">
                    {label}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="mt-8 font-serif text-gray-800 text-base md:text-lg leading-relaxed space-y-4">

          </div>
        </div>
      </section>


      {/* ══════════ "WE GIVE YOU THE IDEAS" — bridges from the empathy
           section's blank-page setup straight into the answer, before the
           Design Center showcase walks through what comes next. Cream
           background carries the empathy voice forward; serif body keeps
           it warm and human, not feature-page. ══════════ */}
      <section id="section-ideas" className="bg-[#faf7f2] border-y border-stone-200">
        <div className="mx-auto w-[90%] max-w-5xl py-14 md:py-20">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="inline-block text-[#00aef7] text-[16px] font-bold uppercase tracking-widest mb-3">
              What if we gave you the ideas?
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              Then, the blank page goes away.
            </h2>
            <p className="mt-4 font-serif text-gray-700 text-base md:text-lg leading-relaxed">
              Our design center AI will give you the ideas. It will create ideas from your profession, industry or any topic you desire, ready to use.
              You can nudge it in a general direction and it will adapt. No googling. No third-party AI. No more &ldquo;what do I say?&rdquo; at 9&nbsp;PM. Ever again.
            </p>
       
          </div>

          {/* Screenshot of the in-app Need Inspiration strip. Path
              expects the file at public/images/home/need-inspiration.png */}
          <figure className="mt-2">
            <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-stone-200/70 bg-white">
              <img
                src="/images/home/need-inspiration.png"
                alt='Design Center "Need Inspiration?" strip — a row of profession-specific post ideas with a "Generate New Idea" tile and "Use This Idea" buttons.'
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
          </figure>

          {/* Three supporting beats — short, scannable, no jargon. */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <h3 className="text-sm font-bold text-gray-900 mb-1">Tuned to your trade.</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Plumbers see plumbing posts. Realtors see realty.
                Accountants see accounting. Every idea is written
                for the work you actually do.
              </p>
            </div>
            <div className="text-center">
              <h3 className="text-sm font-bold text-gray-900 mb-1">Crowd-sourced from your profession.</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Ideas other businesses in your field are using
                right now &mdash; the kinds of posts their
                customers actually engage with.
              </p>
            </div>
            <div className="text-center">
              <h3 className="text-sm font-bold text-gray-900 mb-1">Run out? Generate a new one.</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Tap &ldquo;Generate New Idea&rdquo; and our AI
                writes a fresh one for your profession on the
                spot. The supply never ends.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* ══════════ DESIGN CENTER SHOWCASE — interactive cards + video ══════════ */}
      <DesignCenterShowcase />


      {/* ══════════ HOW IT WORKS ══════════ */}
      <section id="section-3" className="bg-gradient-to-br from-[#00aef7] to-[#0088cc] text-white">
        <div className="mx-auto w-[90%] max-w-6xl py-10 md:py-12">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <h2 className="text-xl md:text-2xl font-bold">From Zero to Published in under 5 Minutes</h2>
            <p className="mt-2 text-white/80 text-sm">No marketing degree. No design skills. You don't even have to come up with your own ideas. We do it for you. If you can tap a screen, you can market your business.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { step: '1', title: 'Sign Up', desc: 'Create your free account in 30 seconds. Look for the signup email, then just answer a few questions and you\'re done. Easy peazy...' },
              { step: '2', title: 'Pick an Idea', desc: 'Open the Design Center and browse content ideas tailored to your profession or industry. Or generate a new one based on your current focus.' },
              { step: '3', title: 'We Build It', desc: 'AI generates headlines, captions, and landing page content, text and call-to-action buttons. All you have to do is fill in the details.' },
              { step: '4', title: 'You Publish It', desc: 'Natively-formatted for each platform. You choose which ones go live and on which platforms.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur flex items-center justify-center mx-auto mb-3">
                  <span className="text-lg font-bold">{item.step}</span>
                </div>
                <h3 className="font-semibold text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-white/75 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ══════════ BUILT-IN ANALYTICS — dual Facebook + TikTok screenshots.
           Written for Meta / TikTok app-review scanners: the words
           "Facebook", "Instagram", "TikTok", "YouTube", "LinkedIn",
           "X", "analytics", "likes", "comments", "shares", "views"
           all appear together in plain text with a visible screenshot
           proving the data is displayed to the user, not just stored. ══════════ */}
      <section id="section-4" className="bg-gray-50 border-y border-gray-100">
        <div className="mx-auto w-[90%] max-w-6xl py-14 md:py-20">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="inline-block text-[#00aef7] text-[11px] font-bold uppercase tracking-widest mb-3">
              Built-in Analytics
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              See how every post performs across every platform.
            </h2>
            <p className="mt-4 text-gray-600 text-sm md:text-base leading-relaxed">
              Once you publish, eHandshake reads engagement metrics back from each connected platform &mdash; <strong>likes, comments, shares, and views</strong> &mdash; so you can see exactly how your creations perform on <strong>Facebook, Instagram, X, LinkedIn, TikTok, and YouTube</strong> in one place. No more hopping between six dashboards.
            </p>
          </div>

          {/* Two screenshots side-by-side — Facebook metrics view + TikTok metrics view. */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            <figure>
              <div className="rounded-2xl overflow-hidden shadow-xl bg-white">
                <img
                  src="/images/team/analytics-dashboardFB.png"
                  alt="eHandshake analytics view for a Facebook post — likes, comments, shares, and views displayed inline with the creation."
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
              <figcaption className="mt-2 text-center text-xs text-gray-500">
                Facebook views &amp; engagement, in context with the post.
              </figcaption>
            </figure>

            <figure>
              <div className="rounded-2xl overflow-hidden shadow-xl bg-white">
                <img
                  src="/images/team/analytics-dashboardTT.png"
                  alt="eHandshake analytics view for a TikTok post — views, likes, comments, and shares displayed inline with the creation."
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
              <figcaption className="mt-2 text-center text-xs text-gray-500">
                TikTok views &amp; engagement, same view, same place.
              </figcaption>
            </figure>
          </div>

          {/* Data-use note — preempts the "what do you do with the data?"
              question that always comes up in Meta / TikTok review. */}
          <p className="mt-10 max-w-2xl mx-auto text-center text-xs md:text-sm text-gray-500 leading-relaxed">
            We only read the engagement data you&rsquo;ve authorized for creations you publish through eHandshake. Your audience&rsquo;s private information is never accessed or stored.
          </p>
        </div>
      </section>


      {/* ══════════ TEAMS LEAD-IN — "you published. now what?" ══════════ */}
      <section id="section-5" className="bg-[#faf7f2] border-y border-stone-200">
        <div className="mx-auto w-[90%] max-w-3xl py-14 md:py-20">
          <div className="text-center mb-8">
            <span className="inline-block text-[#00aef7] text-[11px] font-bold uppercase tracking-widest mb-3">
              You Published. Now What?
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              Congratulations!! Your creation is ready to post on all social platforms.
            </h2>
          </div>

          <div className="font-serif text-gray-700 text-base md:text-lg leading-relaxed space-y-5">
            <p>
              But here&rsquo;s the quieter question: <em>who&rsquo;s actually going to see it after you post it?</em>
            </p>
            <p>
              Only the followers you already have. The same audience you had
              yesterday, last month, last year. And every platform&rsquo;s algorithm
              decides whether <em>they</em> even see it in their feed or not.
            </p>
            <p>
              To grow, you need new eyes. More people encountering your name,
              your face, your business &mdash; an audience that didn&rsquo;t exist
              for you until today.
            </p>
          </div>

          <div className="mt-8 text-center">
            <p className="font-sans text-xl md:text-2xl font-semibold text-[#00aef7]">
              Let us talk about what happens to your post... understand the algorithms and how they work.
            
            </p>
          </div>
        </div>
      </section>


      {/* ══════════ ORGANIC REACH 101 — educational reach math by platform.
           Frames algorithmic distribution as mechanics rather than fault,
           so the section reads as factual to platform reviewers while still
           making the team-distribution case clear to small-business owners. ══════════ */}
      <section id="section-reach" className="bg-white border-y border-gray-100">
        <div className="mx-auto w-[90%] max-w-6xl py-14 md:py-20">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="inline-block text-[#00aef7] text-[11px] font-bold uppercase tracking-widest mb-3">
              Organic Reach 101
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              How far does a single post travel?
            </h2>
            <p className="mt-4 text-gray-600 text-sm md:text-base leading-relaxed">
              Every platform&rsquo;s algorithm shows posts to viewers most likely
              to engage with them &mdash; based on past interactions, content type,
              and signals like saves and shares. For a typical small-business
              page, that means each post reaches only a small fraction of its followers.
              Here&rsquo;s roughly the math, by platform.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-3xl mx-auto">
            <ReachCard
              platform="Facebook"
              accent="#1877F2"
              seen={5}
              caption="Newsfeeds prioritize content from accounts viewers interact with often — friends, family, and pages they engage with regularly."
            />
            <ReachCard
              platform="Instagram"
              accent="#E4405F"
              seen={8}
              caption="Same algorithmic family as Facebook. Reels typically distribute wider than feed posts; saves and shares are the strongest signals."
            />
            <ReachCard
              platform="X"
              accent="#000000"
              seen={8}
              caption="Reply velocity in the first 30 minutes determines whether the post enters non-followers' For You feeds. Bookmarks and shares carry similar weight."
            />
            <ReachCard
              platform="LinkedIn"
              accent="#0A66C2"
              seen={2}
              caption="Dwell time is the dominant signal. Posts that hold attention 10+ seconds reach wider audiences; the rest cycle through quickly."
            />
          </div>

          <p className="mt-14 text-center text-xl md:text-l font-bold text-gray-900 max-w-3xl mx-auto leading-snug">
            A single account&rsquo;s organic reach has a ceiling.
            <br className="hidden sm:block" />
            <span className="text-[#00aef7]"> More people interacting with your post raises that ceiling exponentially.</span>
          </p>

          <div className="mt-10 max-w-2xl mx-auto font-serif text-gray-700 text-base md:text-lg leading-relaxed text-center space-y-4">
            <p>
              These dynamics keep feeds personally relevant for billions of
              users. The trade-off for a small-business page: posting more
              often doesn&rsquo;t change the math much &mdash; what changes
              the math is more people interacting with your post.
            </p>
            <p className="font-sans font-semibold text-gray-900 text-lg md:text-xl">
              More voices. Broader reach. Same post.
            </p>
          </div>

          <p className="mt-10 text-center text-sm text-[#00aef7] font-semibold tracking-wide">
            So, how do you do that? &nbsp;
            <span className="text-gray-500 font-normal">keep scrolling.</span>
          </p>
        </div>
      </section>


      {/* ══════════ DRIP MARKETING vs DRIP NETWORKING ══════════ */}
      <section className="bg-white">
        <div className="mx-auto w-[90%] max-w-5xl py-14 md:py-20">

          {/* ─── Opening story ─── */}
          <div className="max-w-2xl mx-auto mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight text-center mb-6">
              What you are doing is called <span className="text-[#00aef7]">Drip Marketing</span>.
            </h2>
            <div className="font-serif text-gray-700 text-base md:text-lg leading-relaxed space-y-4">
              <p>
                It is a real thing. Google it. You have done it your entire career. You just may not know it by it&rsquo;s real name.
              </p>
              <p>
                Every business card. Every networking event. Every Facebook post about your work. Every email signature, every Google review, every storefront sign or yard sign. Each one drips your name in front of someone new, hopefully. A few people remember it. Most don&rsquo;t. That&rsquo;s the math: a steady drip, one impression at a time.
              </p>
              <p>
                It&rsquo;s the oldest marketing strategy there is, and it works. Stick with it long enough and the people who need what you do this month start to recognize the name they&rsquo;ve seen three times before.
              </p>
              <p className="text-gray-900 font-medium">
                The catch is that you can only drip so far on your own.
              </p>
            </div>
          </div>

          {/* ─── DRIP MARKETING block ─── */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <span className="inline-block text-gray-500 text-[11px] font-bold uppercase tracking-widest mb-2">
                Drip Marketing
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                One billboard. One road.
              </h3>
            </div>

            <div className="max-w-md mx-auto mb-6">
              <BillboardScene />
            </div>

            <p className="text-center text-gray-600 text-sm md:text-base mb-10 max-w-md mx-auto">
              The neighborhoods 10 streets over never see it.
            </p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-8 max-w-xl mx-auto">
              <DotGrid100 highlighted={5} />
              <div className="text-center md:text-left">
                <p className="text-3xl md:text-4xl font-bold text-gray-900 leading-none">
                  <span className="text-[#00aef7]">5</span> of every 100
                </p>
                <p className="text-sm text-gray-700 mt-1.5 mb-3">followers see your post</p>
                <p className="text-xs text-gray-500 leading-relaxed max-w-xs">
                  That&rsquo;s typical Facebook organic reach. Have 500 followers? About 25 see it. 2,000? About 100. The ratio is the same. The ceiling is real.
                </p>
              </div>
            </div>
          </div>

          {/* Visual divider between sections */}
          <div className="max-w-md mx-auto border-t border-gray-200 mb-16" />

          {/* ─── DRIP NETWORKING block ─── */}
          <div className="mb-12">
            <div className="text-center mb-8">
              <span className="inline-block text-[#00aef7] text-[14px] font-bold uppercase tracking-widest mb-2">
                Drip Networking<sup className="text-xs">&trade;</sup>
              </span>
            <p className="text-center text-gray-600 text-sm md:text-base mb-12 max-w-md mx-auto">
              Now imagine that same post being shared to 5 teammate&rsquo;s feeds. Then 10 teammates. Then 50. It's just math. You win. Your teammates win. The people who need your work win.
            </p>
            </div>

            {/* 5×1 row of mini billboards — same post, different roads.
                 Each scene varies sun position + building palette + vehicle
                 colors so they read as five distinct neighborhoods. */}
            <div className="grid grid-cols-5 gap-1 md:gap-2 mb-6 max-w-4xl mx-auto">
              <BillboardSceneMini sunX={30}  sunY={28} buildingPalette={['#fde68a', '#fcd34d', '#e2e8f0', '#cbd5e1']} vehicleColors={['#dc2626', '#1e293b', '#f1f5f9', '#2563eb', '#eab308']} vehicleX={[5,  65, 122, 35, 132]} />
              <BillboardSceneMini sunX={85}  sunY={20} buildingPalette={['#bfdbfe', '#cbd5e1', '#fde68a', '#e2e8f0']} vehicleColors={['#2563eb', '#f1f5f9', '#16a34a', '#94a3b8', '#dc2626']} vehicleX={[18, 78, 115, 50, 145]} />
              <BillboardSceneMini sunX={130} sunY={20} buildingPalette={['#cbd5e1', '#e2e8f0', '#bfdbfe', '#a7f3d0']} vehicleColors={['#16a34a', '#475569', '#eab308', '#2563eb', '#f1f5f9']} vehicleX={[10, 60, 130, 28, 122]} />
              <BillboardSceneMini sunX={170} sunY={28} buildingPalette={['#e2e8f0', '#bfdbfe', '#fde68a', '#cbd5e1']} vehicleColors={['#eab308', '#0f172a', '#dc2626', '#16a34a', '#2563eb']} vehicleX={[22, 72, 118, 60, 135]} />
              <BillboardSceneMini sunX={185} sunY={36} buildingPalette={['#fde68a', '#bfdbfe', '#cbd5e1', '#a7f3d0']} vehicleColors={['#dc2626', '#1e3a8a', '#cbd5e1', '#eab308', '#16a34a']} vehicleX={[12, 70, 128, 38, 150]} />
            </div>

              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-6">
                Same post. <span className="text-[#00aef7]">Many roads.</span>
              </h3>

            {/* 5×2 grid of profession cards — 10 teammates, each reaching 5/100 */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 md:gap-3 max-w-4xl mx-auto mb-10">
              {PROFESSIONS.map(p => (
                <ProfessionCard key={p.name} name={p.name} icon={p.icon} />
              ))}
            </div>

            {/* Closing math — 10x reach summary */}
            <div className="text-center max-w-xl mx-auto">
              <p className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-2">
                <span className="text-[#00aef7]">5</span> from each = <span className="text-[#00aef7]">50</span> different people
              </p>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                Same post. No extra work from you.{' '}
                <span className="font-semibold text-gray-900">10× the reach</span> you&rsquo;d have on your own.
              </p>
            </div>
          </div>

          {/* Payoff line */}
          <p className="text-center text-xl text-[#00aef7] font-semibold tracking-wide mt-8">
            That&rsquo;s Drip Networking&trade; &mdash; Drip Marketing&rsquo;s big brother.
          </p>
        </div>
      </section>


      {/* ══════════ TEAMS ══════════ */}
      <section id="section-6">
        <div className="mx-auto w-[90%] max-w-6xl py-10 md:py-12">
          <SectionHeading
            eyebrow="Grow Together"
            title="Team Up. Cross-Promote. Win."
            subtitle="Join a team of local, non-competing businesses. When one teammate creates a post, everyone can share it, like it and comment on it — multiplying reach without multiplying effort. Don't let the platform algorithms decide who sees your work. Build a human network that does it for you."
          />
          <div className="grid md:grid-cols-3 gap-4">
            <FeatureCard
              icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" /></svg>}
              title="Build Your Team"
              description="Invite complementary local businesses — the roofer, the electrician, the HVAC tech. Support each other's marketing with zero extra work."
            />
            <FeatureCard
              icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" /></svg>}
              title="Share Each Other's Content"
              description="When your teammate posts a tip about water heaters, you can share it to your audience — and they'll do the same for you."
            />
            <FeatureCard
              icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" /></svg>}
              title="Grow Collectively"
              description="Your team score combines everyone's reviews. A strong team means more trust, more visibility, and more customers for all."
            />
          </div>

          {/* ─── Real team screenshot — Winter Park Marlins.
              File lives at public/images/team/team-dashboard.png ─── */}
          <div className="mx-auto max-w-[900px] mt-10 md:mt-14">
            <div className="rounded-2xl overflow-hidden shadow-xl bg-white">
              <img
                src="/images/team/team-dashboard.png"
                alt="eHandshake team dashboard showing the Winter Park Marlins team — captain, 11 teammates, team score 4.99, connected social channels, and a live activity feed of teammates' recent posts and shares."
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
            <p className="mt-3 text-center text-xs text-gray-500">
              When anyone on this team posts, it drips through the entire group&rsquo;s combined audience.
            </p>
          </div>
        </div>
      </section>


      {/* ══════════ TECH PROVIDER ══════════ */}
      <section id="section-7">
        <div className="mx-auto w-[90%] max-w-6xl py-10 md:py-12">
          <SectionHeading
            eyebrow="Technology Platform"
            title="Built for Businesses. Powered by Integrations."
            subtitle="eHandshake is a social media management platform that connects to Facebook, Instagram, X, YouTube, LinkedIn, and TikTok via official APIs. We publish content on behalf of our users with their explicit authorization through OAuth."
          />
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">How We Access User Data</h3>
              <ul className="space-y-1.5 text-xs text-gray-600">
                <li className="flex gap-2"><span className="text-[#00aef7] font-bold">1.</span> User signs up and connects social accounts via OAuth</li>
                <li className="flex gap-2"><span className="text-[#00aef7] font-bold">2.</span> We request only needed permissions: page publishing, media upload, engagement metrics</li>
                <li className="flex gap-2"><span className="text-[#00aef7] font-bold">3.</span> User creates or selects content in our Design Center</li>
                <li className="flex gap-2"><span className="text-[#00aef7] font-bold">4.</span> We publish to their connected accounts using official platform APIs</li>
                <li className="flex gap-2"><span className="text-[#00aef7] font-bold">5.</span> We fetch engagement data (likes, comments, shares, views) to show performance</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Platform & Security</h3>
              <ul className="space-y-1.5 text-xs text-gray-600">
                {[
                  'OAuth 2.0 authentication — we never store passwords',
                  'SSL/TLS encryption on all connections',
                  'Users control which accounts are connected',
                  'Data deletion available on request',
                  'Hosted on AWS with automated backups',
                  'Real-time Matrix messaging (end-to-end encrypted)',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0"><path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" /></svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>


      {/* ══════════ CTA ══════════ */}
      <section id="section-8" className="bg-gray-900">
        <div className="mx-auto w-[90%] max-w-4xl py-10 md:py-14 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-white leading-tight">
            Stop struggling with marketing.<br />Start growing your business.
          </h2>
          <p className="mt-3 text-gray-400 text-sm max-w-xl mx-auto">
            Join other local professionals who use eHandshake to create professional content, publish to every platform, and build their reputation &mdash; Collectively &mdash; without the headache.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <a
              href="/?modal=signup"
              className="inline-flex items-center px-6 py-3 rounded-lg bg-[#00aef7] text-white text-sm font-semibold hover:bg-[#0098d8] transition-colors shadow-lg shadow-[#00aef7]/25"
            >
              Get Started Free
            </a>
            <a
              href={`${PUBLIC_SITE}/search`}
              className="inline-flex items-center px-6 py-3 rounded-lg border border-gray-600 text-gray-300 text-sm font-semibold hover:bg-gray-800 transition-colors"
            >
              Browse Directory
            </a>
          </div>
        </div>
      </section>

      {/* ══════════ FLOATING SCROLL-TO-TOP BUTTON ══════════ */}
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Scroll to top"
        title="Scroll to top"
        className={`fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-[#00aef7] hover:bg-[#0098d8] shadow-lg flex items-center justify-center text-white transition-all duration-300 ${
          showScrollTop ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none translate-y-2'
        }`}
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>

    </main>
  );
};

export default Home;
